import {
  GuestJourneyMovementDirection,
  GuestJourneyMovementSankey,
  GuestJourneyMovementSankeyLink,
  GuestJourneyMovementSankeyNode,
  GuestJourneySankeyFlow,
  GuestJourneySankeyResponse,
} from 'api/reports-service/guest-journey-reports.types';
import { Movement } from 'components/movement-indicator/movement-indicator.component';

export type BuildMovementSankeyOptions = {
  /** ID for the center node (selected stage enum value or offer id). */
  centerId: string;
  /** Label for the stayed pass-through band (FE-owned i18n). */
  stayedLabel: string;
  /**
   * Offer Impact variant: outgoing flows arrive per destination stage, but
   * the design's sankey shows them aggregated into directional movement
   * buckets ("Moved Up", "At Risk", …). Groups outgoing flows by their
   * movement text and sums the counts; the table still renders the
   * per-stage flows.
   */
  aggregateOutgoingByMovement?: boolean;
};

/**
 * Color bucket for a band, from the flow's movement. Same polarity rules as
 * the MovementIndicator chip (upward green, downward red, invertColor flips,
 * STABLE always neutral) so a flow's band can never disagree with its table
 * chip.
 */
export const toGraphDirection = (
  movement: Movement,
): GuestJourneyMovementDirection => {
  if (movement.direction === 'STABLE') return 'neutral';
  const isUp = movement.direction.startsWith('UP');
  const up = movement.invertColor ? !isUp : isUp;
  return up ? 'up' : 'down';
};

const aggregateByMovement = (
  flows: GuestJourneySankeyFlow[],
): GuestJourneySankeyFlow[] => {
  const buckets = new Map<string, GuestJourneySankeyFlow>();
  flows.forEach(flow => {
    // Buckets are keyed by the BE-sent movement text until the BE adds a
    // bucket id (report-fields product question #4).
    const existing = buckets.get(flow.movement.text);
    if (existing) {
      existing.count += flow.count;
    } else {
      buckets.set(flow.movement.text, {
        ...flow,
        stage: flow.movement.text,
      });
    }
  });
  return [...buckets.values()];
};

/**
 * Vertical band order within each column: by color first — green (`up`) →
 * grey (`neutral`) → red (`down`) — then by value (flow count) descending
 * within each color. Applied to both the incoming and outgoing columns so they
 * read the same way; the chart is told to honor it with `nodeOptions.sort:
 * 'fixed'` (see the MovementSankey series), which pins node order to this array
 * instead of running MUI's crossing-minimization layout.
 */
const DIRECTION_ORDER: Record<GuestJourneyMovementDirection, number> = {
  up: 0,
  neutral: 1,
  down: 2,
};

/** One placed band (a flow, or the stayed pass-through) on one side. */
type SankeyBand = {
  nodeId: string;
  label: string;
  direction: GuestJourneyMovementDirection;
  value: number;
  source: string;
  target: string;
};

const compareBands = (a: SankeyBand, b: SankeyBand) =>
  DIRECTION_ORDER[a.direction] - DIRECTION_ORDER[b.direction] ||
  b.value - a.value;

/**
 * Synthesizes the node/link graph the chart renders from the BE's bucket
 * payload (`{ stayedCount, incoming, outgoing }`). One node + link per flow;
 * node ids are side-prefixed (`in:`/`out:`) because the same stage can appear
 * on both sides of the center. `stayedCount` renders as a neutral band passing
 * through the center via a phantom node pair.
 *
 * Each column's bands are ordered by `compareBands` (green → grey → red, then
 * value descending) — the stayed band is grey, so it sorts into the grey group
 * by its count. The chart preserves this order via `nodeOptions.sort: 'fixed'`.
 *
 * Tolerates a partial payload (`useSearchQuery` yields `{}` while the query
 * is in flight) by treating missing flow arrays as empty.
 */
export const buildMovementSankey = (
  data: Partial<GuestJourneySankeyResponse>,
  {
    centerId,
    stayedLabel,
    aggregateOutgoingByMovement = false,
  }: BuildMovementSankeyOptions,
): GuestJourneyMovementSankey => {
  const stayedCount = data.stayedCount ?? 0;

  const flowBand = (
    flow: GuestJourneySankeyFlow,
    side: 'in' | 'out',
  ): SankeyBand => {
    const nodeId = `${side}:${flow.stage}`;
    return {
      nodeId,
      label: flow.stage,
      direction: toGraphDirection(flow.movement),
      value: flow.count,
      source: side === 'in' ? nodeId : centerId,
      target: side === 'in' ? centerId : nodeId,
    };
  };

  // A column's bands: its flows plus (when non-zero) the stayed pass-through,
  // sorted green → grey → red, then value descending.
  const buildColumn = (
    flows: GuestJourneySankeyFlow[],
    side: 'in' | 'out',
  ): SankeyBand[] => {
    const bands = flows.map(flow => flowBand(flow, side));
    if (stayedCount > 0) {
      const stayedId = `${side}:stayed`;
      bands.push({
        nodeId: stayedId,
        label: stayedLabel,
        direction: 'neutral',
        value: stayedCount,
        source: side === 'in' ? stayedId : centerId,
        target: side === 'in' ? centerId : stayedId,
      });
    }
    return bands.sort(compareBands);
  };

  const columns = [
    ...buildColumn(data.incoming ?? [], 'in'),
    ...buildColumn(
      aggregateOutgoingByMovement
        ? aggregateByMovement(data.outgoing ?? [])
        : data.outgoing ?? [],
      'out',
    ),
  ];

  const nodes: GuestJourneyMovementSankeyNode[] = columns.map(band => ({
    id: band.nodeId,
    label: band.label,
    direction: band.direction,
  }));
  const links: GuestJourneyMovementSankeyLink[] = columns.map(band => ({
    source: band.source,
    target: band.target,
    value: band.value,
    direction: band.direction,
  }));

  if (!links.length) return { centerId, nodes: [], links: [] };

  return {
    centerId,
    nodes: [
      ...nodes,
      // The center node shows no visible label — the offer/stage name is in the
      // chart header, so an on-node label would just overlay the flows. MUI
      // forces an empty label to the node id (getSeriesWithDefaultValues), so
      // use a zero-width space: truthy (not replaced by the id) but invisible.
      {
        id: centerId,
        label: String.fromCharCode(0x200b),
        direction: 'neutral',
      },
    ],
    links,
  };
};
